// Where material is trying to get to, as a **field over the board**.
//
// This replaced "rank the machines by kind, then walk toward the nearest one by
// Chebyshev distance". That rule had three holes and they compounded:
//
//  - **It did not know what a route was.** Distance was measured through walls,
//    through enemy armies and across gaps no belt and no bucket brigade could
//    cross. A rook alone on the far side of the board pulled every unit on it
//    toward a sink nothing could reach.
//  - **The nearest sink of a kind is not the sink that wants it.** A refinery
//    outranked every foundry on the board however far away it was, and among
//    refineries the winner was whichever happened to be closest — which changes
//    the moment anybody moves.
//  - **Both together made material shuttle.** The winning sink was recomputed
//    every tick against a *moving* board, so a king and a bishop either side of
//    the midline between them handed one ingot back and forth for the rest of
//    the game. Reported from play, and the ingot never changed grade — neither
//    machine could cook it, so both were merely passing it toward a rook that
//    was never getting it.
//
// So: build a cost field by searching **backwards from the machines that can
// actually use this material, over the network that actually exists** — hand-off
// links, lanes, and the grabs off them. Every hand-off then goes strictly
// downhill. Material cannot shuttle, because within a tick the field is one
// fixed function of the board and a strict descent has nowhere to loop; and it
// cannot chase what it cannot reach, because an unreachable machine contributes
// nothing to any square.
//
// **The field is capability, not occupancy.** A source is a machine that *could*
// use the unit — not one with a free slot this instant. Two reasons: a full oven
// still wants ore, so material should queue behind it rather than divert and
// come back when it empties; and a field that changes as buffers fill cannot be
// computed once a tick. Whether anything will take it *right now* is asked at
// the moment of delivery, by `accepts`.
//
// Nothing here knows what a refinery is. The semantics come in through
// `RoutingRules`, which `machines.ts` implements — otherwise this module and
// that one import each other.

import { DIR_OFFSET, pieceColor } from "../board";
import { hasCell, pieceAt, type Position } from "../position";
import { materialLinks } from "./adjacency";
import {
  beltAcceptsFrom,
  beltDirAt,
  beltOnAt,
  beltOwnerAt,
  hasStructure,
  type IndustryState,
  liveSquares,
  NO_BELT,
  structureAt
} from "./state";

/**
 * What a square is worth as a destination, and what it will hold.
 *
 * Three questions rather than one because they are asked at different moments:
 * `demand` and `canHold` build the field and must not depend on how full
 * anything is, or the field would be stale before it was used; `accepts` is
 * asked once, of the square material is about to be handed to.
 */
export interface RoutingRules {
  /**
   * How badly this square wants the unit, as a starting cost — lower is
   * keener, `Infinity` for a square that is not a destination at all. Distance
   * is added to it, so the gap between two tiers is the number of squares one
   * is worth walking past the other.
   */
  demand(square: number): number;
  /** Would this square ever hold the unit, ignoring how full it is now? */
  canHold(square: number): boolean;
  /** Will it take it *this instant*? Buffers, stock, everything. */
  accepts(square: number): boolean;
  /**
   * Would this square reach over and take the unit *off a lane*?
   *
   * Narrower than `canHold` and deliberately so — only a consumer grabs, and
   * only from a lane of its own. A knight beside a trunk line will hold
   * material handed to it and cannot lift any, so a field that treated it as a
   * way off the lane would route cargo toward a stop that does not exist.
   */
  canGrab(square: number): boolean;
  /** Is this lane one the grabbing rule counts as ours? */
  ownsLane(square: number): boolean;
  /**
   * Would this square *process* the unit — raise its grade or spend it on a
   * recipe — rather than merely hold it?
   *
   * A tie-break and nothing more. Where two routes cost the same, the one with
   * a stage on it is the one an assembly line wants; where they do not, cost
   * still wins, because a processor two squares further away is two squares
   * further away.
   */
  processes(square: number): boolean;
}

export interface DemandField {
  /** Cost from this square to the machine that wants the unit most. */
  cost: Map<number, number>;
  /**
   * What each destination is worth on its own.
   *
   * Kept beside the costs for one test: a square whose cost *is* its own demand
   * is the destination, and a destination never hands the unit on. That is the
   * whole of what stops two ovens passing one ingot sideways forever — the old
   * rule compared distances and two adjacent sinks are both at zero.
   */
  own: Map<number, number>;
}

/** Where material can go from a square, and how far it is from being wanted. */
export interface Route {
  square: number;
  /** True for a piece or a building, false for a lane. */
  machine: boolean;
}

/**
 * A field per distinct unit, for one tick.
 *
 * The key is the packed unit word, which is exactly the right grain: two units
 * with the same grade and the same refinery provenance are interchangeable to
 * every rule in the game, and units carry no identity beyond those two things.
 * Valid for as long as the board's *shape* is — which is a whole tick, since
 * nothing in the machine passes moves a piece or lays a part.
 *
 * Two maps because there are two kinds of field. `full` is what routing reads:
 * every machine's demand includes how far its own output would then have to
 * travel. `flat` is that second question answered — the same search with the
 * lookahead switched off, which is what keeps the depth at one and stops a
 * chain of machines asking each other the question forever.
 */
export interface DemandCache {
  full: Map<number, DemandField>;
  /** Keyed by the unit made *and* the machine that would make it. */
  flat: Map<string, DemandField>;
  /**
   * Who each square hands material to, by square.
   *
   * **A field is built per unit *word*, and the links are the same for all of
   * them.** `materialLinks` is a pure function of the board — eight map
   * lookups and a fresh array — and it was being recomputed for every square
   * of every field, so a tick with four distinct grades in flight paid for it
   * four times over and threw three of the answers away. Measured on a
   * `wide`-board game, `materialLinks` and the `edgesFrom` around it are about
   * a tenth of the whole run.
   *
   * Safe for exactly as long as a `DemandCache` is: it is made in
   * `runMachines` and thrown away with the tick, and the board cannot change
   * underneath it — which is the same lifetime argument the two fields above
   * already rest on, and the reason this belongs here rather than on the
   * industry.
   */
  links: Map<number, readonly number[]>;
  /**
   * Every square material can occupy, derived once for the tick.
   *
   * **`networkSquares` is a Set, a spread and a *sort*, and it ran per
   * field** — which is per distinct unit word, so a tick with four grades in
   * flight built the same list four times and threw three away. Exactly the
   * redundancy `links` was added for, one level up: both are pure functions of
   * the board, and the board cannot change under a tick.
   */
  squares?: number[];
}

export const createDemandCache = (): DemandCache => ({
  full: new Map(),
  flat: new Map(),
  links: new Map()
});

/**
 * Every square material can occupy: a piece, a building, or a part.
 *
 * Sorted, because two clients derive this independently from one log
 * (PLAN §5.5) and Map order is agreement by luck.
 */
const networkSquares = (position: Position, industry: IndustryState): number[] =>
  liveSquares(
    position.board,
    industry.beltDir,
    industry.structures
  );

/** A running part somebody who is not an enemy owns. */
const isLane = (
  industry: IndustryState,
  sq: number,
  isEnemy: (side: number) => boolean
): boolean => {
  const owner = beltOwnerAt(industry, sq);
  return (
    beltDirAt(industry, sq) !== NO_BELT &&
    owner >= 0 &&
    !isEnemy(owner) &&
    beltOnAt(industry, sq) === 1
  );
};

/**
 * Where a unit standing on `sq` could be next turn — the forward edges of the
 * material network.
 *
 * Four kinds, and they are the four ways material moves in the tick:
 * a hand-off to a touching machine, a load onto a touching lane, a lane
 * carrying it one square, and a consumer grabbing it off a lane it touches.
 * Nothing else exists, which is why a field built on these is exactly as
 * connected as the factory is.
 */
function edgesFrom(
  position: Position,
  industry: IndustryState,
  sq: number,
  rules: RoutingRules,
  isEnemy: (side: number) => boolean,
  out: number[],
  /** Per-tick link memo. Absent for a caller that has no cache to hand. */
  cache?: DemandCache
): void {
  let links = cache?.links.get(sq);
  if (!links) {
    links = materialLinks(position, industry, sq);
    cache?.links.set(sq, links);
  }

  // A piece or a building is holding it, so it can hand it on or put it down on
  // a lane it touches. Any piece of ours, not only one that could *receive*
  // material — a pawn takes delivery from nothing but the ground and still has
  // to be able to pass on what it digs.
  const piece = pieceAt(position, sq);
  const structure = structureAt(industry, sq);
  const holder =
    (piece !== 0 && !isEnemy(pieceColor(piece))) ||
    (structure !== undefined && !isEnemy(structure.owner));

  if (holder) {
    for (const adj of links) {
      if (rules.canHold(adj)) out.push(adj);
      // A lane pointing back at us is not a way out — see `loadingBelt`.
      else if (
        isLane(industry, adj, isEnemy) &&
        adj + DIR_OFFSET[beltDirAt(industry, adj)] !== sq
      ) {
        out.push(adj);
      }
    }
  }

  if (!isLane(industry, sq, isEnemy)) return;

  // A lane moves material one square, and **only the way it points**. This is
  // the edge the old rule had no concept of at all: it measured straight-line
  // distance to a sink and never asked whether any track led there.
  const next = sq + DIR_OFFSET[beltDirAt(industry, sq)];
  if (hasCell(position, next) || hasStructure(industry, next)) {
    if (rules.canHold(next) || isLane(industry, next, isEnemy)) out.push(next);
  }

  // A consumer beside the lane reaches over and lifts it off. Its own lane
  // only: taking off an ally's is theft, not help.
  if (!rules.ownsLane(sq)) return;
  for (const adj of links) {
    if (adj !== sq && rules.canGrab(adj)) out.push(adj);
  }
}

/**
 * The cost field: how far each square is from the machine that wants this unit
 * most, searched backwards from the machines themselves.
 *
 * Uniform edge costs and small integer starting costs, so this is a bucket
 * queue rather than a heap — and the distances it settles on are unique, which
 * is what lets the search skip any tie-breaking of its own. Where two routes
 * are equally good the *choice* is made in `routeFrom`, deterministically.
 */
export function demandField(
  position: Position,
  industry: IndustryState,
  isEnemy: (side: number) => boolean,
  rules: RoutingRules,
  /**
   * The tick's memo, so the links are derived once for all of its fields.
   *
   * Optional, because a caller with nowhere to keep one still has to get the
   * same field — the same argument `demandFor` already makes about building on
   * a throwaway rather than skipping a term.
   */
  cache?: DemandCache
): DemandField {
  let squares = cache?.squares;
  if (!squares) {
    squares = networkSquares(position, industry);
    if (cache) cache.squares = squares;
  }

  // Forward edges first, then inverted: the search runs from the destinations
  // outwards, and "who can reach me" is not a question a square can answer
  // about itself.
  const into = new Map<number, number[]>();
  const forward: number[] = [];
  for (const sq of squares) {
    forward.length = 0;
    edgesFrom(position, industry, sq, rules, isEnemy, forward, cache);
    for (const to of forward) {
      const list = into.get(to);
      if (list) list.push(sq);
      else into.set(to, [sq]);
    }
  }

  const cost = new Map<number, number>();
  const own = new Map<number, number>();
  const buckets: number[][] = [];
  /**
   * The distinct tiers something was actually seeded at, in order.
   *
   * **This is a bucket queue indexed by cost, and one tier is a million.** A
   * store ranks last on purpose — `RULES.demand.store` is far larger than any
   * board is wide, which is what keeps a warehouse from swallowing the ore an
   * oven wanted (PLAN §14's most important tuning decision). Indexed straight
   * into `buckets`, that number is not a rank, it is an *array position*: the
   * walk below ran `c` from nought to a million and did nothing for
   * 999,99x of them — per field, per distinct unit word, **per tick**.
   *
   * Measured on a `wide` board, six turns: 0.65s of planning with no store on
   * the board and **17s with one**, and it made no difference whether the
   * store was beside the army or in the far corner. Distance was never the
   * variable; owning a warehouse at all was. `demandField` was 87.5% of the
   * whole profile.
   *
   * Costs only ever grow by one from a settled bucket, so the occupied indices
   * are contiguous runs starting at the seeded tiers — walk the run, then jump
   * to the next tier rather than counting to it.
   */
  const tiers: number[] = [];

  for (const sq of squares) {
    const want = rules.demand(sq);
    if (!Number.isFinite(want)) continue;
    own.set(sq, want);
    const at = want as number;
    if (buckets[at]) buckets[at].push(sq);
    else {
      buckets[at] = [sq];
      tiers.push(at);
    }
  }
  tiers.sort((a, b) => a - b);

  let next = 0;
  for (let c = tiers.length > 0 ? tiers[0] : 0; tiers.length > 0; ) {
    const bucket = buckets[c];
    if (bucket) {
      for (const sq of bucket) {
        // Settled already at a lower cost by an earlier bucket.
        if (cost.has(sq)) continue;
        cost.set(sq, c);
        for (const from of into.get(sq) ?? []) {
          if (cost.has(from)) continue;
          (buckets[c + 1] ??= []).push(from);
        }
      }
    }
    // The run continues while the next index is occupied — including a bucket
    // this pass has just created, which is why it is asked *after* the work.
    if (buckets[c + 1]) {
      c += 1;
      continue;
    }
    while (next < tiers.length && tiers[next] <= c) next += 1;
    if (next >= tiers.length) break;
    c = tiers[next];
  }

  return { cost, own };
}

/**
 * Hops from `from` to the nearest square that wants this material, or -1.
 *
 * The same network as `demandField` walks, searched **forwards from one square**
 * and stopped at the first destination. Where a field answers "for every square,
 * how far", this answers "for one square, how far" — and when the question is
 * only ever asked of two or three machines, building a whole field to read three
 * entries out of it is most of the cost for none of the answer.
 *
 * `from` is never its own answer. A machine that is both oven and forge would
 * otherwise report nought and call itself a factory.
 */
export function hopsToDemand(
  position: Position,
  industry: IndustryState,
  isEnemy: (side: number) => boolean,
  rules: RoutingRules,
  from: number,
  limit: number,
  /** The tick's memo, when the caller has one. */
  cache?: DemandCache
): number {
  const seen = new Set<number>([from]);
  let frontier = [from];
  const out: number[] = [];

  for (let hop = 1; hop <= limit; hop++) {
    const next: number[] = [];
    for (const sq of frontier) {
      out.length = 0;
      edgesFrom(position, industry, sq, rules, isEnemy, out, cache);
      for (const to of out) {
        if (seen.has(to)) continue;
        seen.add(to);
        if (Number.isFinite(rules.demand(to))) return hop;
        next.push(to);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return -1;
}

/**
 * Where this square should send the unit, or null to hold it.
 *
 * Strictly downhill, with one exception: a **destination** may be handed the
 * unit at equal cost. That is what lets a piece standing between a full oven
 * and a foundry give it to the foundry — the oven is what set the local cost,
 * so nothing beats it, and without the exception the material would wait for a
 * slot that is not coming this turn. It cannot reopen the shuttling problem,
 * because a destination consumes rather than forwards, and because a square
 * that is *itself* the destination never gets here at all.
 */
export function routeFrom(
  position: Position,
  industry: IndustryState,
  sq: number,
  rules: RoutingRules,
  field: DemandField,
  isEnemy: (side: number) => boolean,
  /** Will a lane take it right now — room, ownership, and not pointing back. */
  laneAccepts: (square: number) => boolean,
  /**
   * The unit is leaving a machine's **out** bank, so this square is not a
   * destination for it whatever its own demand says — and anywhere that is
   * reachable at all is an improvement on staying.
   */
  outbound = false
): Route | null {
  const settled = field.cost.get(sq);
  // Nothing anywhere wants this. Holding it is the honest outcome: the old rule
  // fell through to "put it on whatever lane is beside you", which routes by
  // the direction the *source* happens to be pointing.
  if (settled === undefined && !outbound) return null;
  // We are the destination.
  if (!outbound && field.own.get(sq) === settled) return null;
  const here = outbound ? Infinity : (settled as number);

  const candidates: {
    square: number;
    machine: boolean;
    cost: number;
    stage: boolean;
  }[] = [];
  for (const adj of materialLinks(position, industry, sq)) {
    const there = field.cost.get(adj);
    if (there === undefined) continue;
    const destination = field.own.get(adj) === there;
    if (!(there < here || (destination && there <= here))) continue;

    // A piece standing on its own part shares the square, so both readings can
    // be live at once — and the machine is the better of the two, being an
    // arrival rather than another hop.
    if (rules.canHold(adj)) {
      candidates.push({ square: adj, machine: true, cost: there, stage: rules.processes(adj) });
    } else if (isLane(industry, adj, isEnemy)) {
      candidates.push({ square: adj, machine: false, cost: there, stage: false });
    }
  }

  // Cheapest first; then **a stage over a stop** — at equal cost the assembly
  // line wants the neighbour that will actually work the material rather than
  // one that would hold it and pass it on again; then a machine over a lane;
  // then by square, because two clients derive this independently (PLAN §5.5).
  candidates.sort(
    (a, b) =>
      a.cost - b.cost ||
      Number(b.stage) - Number(a.stage) ||
      Number(b.machine) - Number(a.machine) ||
      a.square - b.square
  );

  for (const { square, machine } of candidates) {
    if (machine ? rules.accepts(square) : laneAccepts(square)) return { square, machine };
  }
  return null;
}

/** Whether a lane at `sq` may be loaded from `from` at all. */
export const laneIsLoadable = (
  industry: IndustryState,
  sq: number,
  from: number,
  isEnemy: (side: number) => boolean
): boolean =>
  beltAcceptsFrom(isEnemy, industry, sq) &&
  sq + DIR_OFFSET[beltDirAt(industry, sq)] !== from;
